import { Mail, Phone, MessageCircle } from "lucide-react";
import InquiryForm from "@/components/InquiryForm";
import useReveal from "@/hooks/useReveal";
import { BRAND, buildWaLink } from "@/lib/constants";

export default function Contact() {
  useReveal();

  return (
    <div data-testid="contact-page" className="pt-28 md:pt-32">
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-12 md:pb-16">
        <div className="reveal max-w-3xl">
          <p className="eyebrow mb-4">Plan My Trip</p>
          <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-[#1F3A5F] leading-[1.05]">
            Let's plan your Ladakh story.
          </h1>
          <p className="mt-6 text-[#5A6B82] leading-relaxed text-base sm:text-lg">
            Tell us a little about your trip — preferred dates, group size,
            must-visit places — and we'll come back with a real plan within
            hours.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-20 md:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-7 reveal">
            <div className="bg-white border border-[#1F3A5F]/10 p-8 md:p-12">
              <InquiryForm source="contact" />
            </div>
          </div>

          <aside className="lg:col-span-5 reveal">
            <div className="bg-[#11233B] text-[#FAF4ED] p-8 md:p-12 h-full">
              <p className="eyebrow mb-3 text-[#E8B89B]">Direct Contact</p>
              <h2 className="font-serif font-light text-3xl md:text-4xl">
                Prefer talking?
              </h2>
              <p className="mt-5 text-[#FAF4ED]/75 leading-relaxed text-sm">
                We're a small specialised team. Reach us directly and a real
                planner will respond — usually within hours.
              </p>

              <div className="mt-10 space-y-6">
                <a
                  href={buildWaLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp"
                  className="flex items-start gap-4 group border-t border-[#FAF4ED]/15 pt-6"
                >
                  <span className="bg-[#25D366] text-white p-3 group-hover:scale-105 transition-transform">
                    <MessageCircle size={18} strokeWidth={1.6} />
                  </span>
                  <span>
                    <span className="block text-[10px] uppercase tracking-[0.22em] text-[#E8B89B]">
                      WhatsApp
                    </span>
                    <span className="block font-serif text-xl mt-1">
                      {BRAND.phone}
                    </span>
                    <span className="block text-xs text-[#FAF4ED]/60 mt-1">
                      Fastest reply · 7 days a week
                    </span>
                  </span>
                </a>

                <a
                  href={`tel:${BRAND.phone.replace(/\s/g, "")}`}
                  data-testid="contact-phone"
                  className="flex items-start gap-4 group border-t border-[#FAF4ED]/15 pt-6"
                >
                  <span className="bg-[#FAF4ED]/10 group-hover:bg-[#FAF4ED]/20 text-[#FAF4ED] p-3 transition-colors">
                    <Phone size={18} strokeWidth={1.6} />
                  </span>
                  <span>
                    <span className="block text-[10px] uppercase tracking-[0.22em] text-[#E8B89B]">
                      Phone
                    </span>
                    <span className="block font-serif text-xl mt-1">
                      {BRAND.phone}
                    </span>
                  </span>
                </a>

                <a
                  href={`mailto:${BRAND.email}`}
                  data-testid="contact-email"
                  className="flex items-start gap-4 group border-t border-[#FAF4ED]/15 pt-6"
                >
                  <span className="bg-[#FAF4ED]/10 group-hover:bg-[#FAF4ED]/20 text-[#FAF4ED] p-3 transition-colors">
                    <Mail size={18} strokeWidth={1.6} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[10px] uppercase tracking-[0.22em] text-[#E8B89B]">
                      Email
                    </span>
                    <span className="block font-serif text-xl mt-1 break-all">
                      {BRAND.email}
                    </span>
                  </span>
                </a>
              </div>

              <div className="mt-12 border-t border-[#FAF4ED]/15 pt-6">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#E8B89B]">
                  Best season for Ladakh
                </p>
                <p className="font-serif text-2xl mt-2 text-[#FAF4ED]/95">
                  May to October
                </p>
                <p className="text-xs text-[#FAF4ED]/60 mt-2">
                  Roads to Pangong, Nubra & Umling La open seasonally — plan
                  early.
                </p>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
